// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { X, Shield, Activity, BarChart3, AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

interface CoinProfileModalProps {
  symbol: string;
  onClose: () => void;
}

interface CoinData {
  hedgeBtc?: any;
  hedgeEth?: any;
  bmsbUsd?: any;
  bmsbBtc?: any;
  priceCompBtc?: any;
  priceCompEth?: any;
}

const CoinProfileModal: React.FC<CoinProfileModalProps> = ({ symbol, onClose }) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [coinData, setCoinData] = useState<CoinData>({});
  const [error, setError] = useState<string | null>(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchCoinData();
  }, [symbol]);

  const fetchCoinData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data from all endpoints in parallel
      const results = await Promise.allSettled([
        axios.get(`${API_BASE}/api/hedge-performers?timeframe=1Y&limit=500`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/hedge-performers-eth?timeframe=1Y&limit=500`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/bull-market-support-band-usd?limit=500`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/bull-market-support-band-btc?limit=500`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/price-comparison-btc?timeframe=1Y`, { headers: getAuthHeaders() }),
        axios.get(`${API_BASE}/api/price-comparison-eth?timeframe=1Y`, { headers: getAuthHeaders() }),
      ]);

      const data: CoinData = {};

      // Hedge BTC data
      if (results[0].status === 'fulfilled') {
        const hedgeCoin = results[0].value.data.performers?.find((c: any) => c.symbol === symbol);
        if (hedgeCoin) data.hedgeBtc = hedgeCoin;
      }

      // Hedge ETH data
      if (results[1].status === 'fulfilled') {
        const hedgeCoin = results[1].value.data.performers?.find((c: any) => c.symbol === symbol);
        if (hedgeCoin) data.hedgeEth = hedgeCoin;
      }

      // BMSB USD data
      if (results[2].status === 'fulfilled') {
        const bmsbCoin = results[2].value.data?.find((c: any) => c.symbol === symbol);
        if (bmsbCoin) data.bmsbUsd = bmsbCoin;
      }

      // BMSB BTC data
      if (results[3].status === 'fulfilled') {
        const bmsbCoin = results[3].value.data?.find((c: any) => c.symbol === symbol);
        if (bmsbCoin) data.bmsbBtc = bmsbCoin;
      }

      // Price Comparison BTC data
      if (results[4].status === 'fulfilled') {
        const priceComp = results[4].value.data.comparisons?.find((c: any) => c.symbol === symbol);
        if (priceComp) data.priceCompBtc = priceComp;
      }

      // Price Comparison ETH data
      if (results[5].status === 'fulfilled') {
        const priceComp = results[5].value.data.comparisons?.find((c: any) => c.symbol === symbol);
        if (priceComp) data.priceCompEth = priceComp;
      }

      setCoinData(data);

      // Check if no data found for this coin
      if (Object.keys(data).length === 0) {
        setError('No data available for this coin');
      }
    } catch (err) {
      console.error('Error fetching coin data:', err);
      setError('Failed to load coin data');
    } finally {
      setLoading(false);
    }
  };

  const getCoinLogoUrl = () => {
    const logoUrl = coinData.hedgeBtc?.logo_url || coinData.hedgeEth?.logo_url || coinData.bmsbUsd?.logo_url;
    if (logoUrl) return logoUrl;
    return `https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`;
  };

  const getCoinName = () => {
    return coinData.hedgeBtc?.name || coinData.hedgeEth?.name || coinData.bmsbUsd?.name || symbol;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto animate-fadeIn">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-md transition-all duration-300"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className={`relative w-full max-w-5xl rounded-3xl shadow-2xl transform transition-all duration-300 scale-100 ${
          isDark
            ? 'bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border border-white/10'
            : 'bg-white'
        }`}>
          {/* Header with Gradient */}
          <div className={`relative px-8 py-6 border-b overflow-hidden ${
            isDark
              ? 'border-white/10 bg-gradient-to-r from-emerald-500/10 via-blue-500/10 to-purple-500/10'
              : 'border-gray-200 bg-gradient-to-r from-orange-50 via-blue-50 to-purple-50'
          }`}>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/5 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-blue-500/5 to-transparent rounded-full blur-3xl"></div>

            <div className="relative flex items-center justify-between">
              <div className="flex items-center space-x-5">
                <div className="relative">
                  <img
                    src={getCoinLogoUrl()}
                    alt={symbol}
                    className={`w-16 h-16 rounded-full ring-4 shadow-lg ${
                      isDark ? 'ring-white/20' : 'ring-gray-200'
                    }`}
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/64';
                    }}
                  />
                  <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    isDark
                      ? 'bg-emerald-500 text-white'
                      : 'bg-orange-500 text-white'
                  }`}>
                    ✓
                  </div>
                </div>
                <div>
                  <h2 className={`text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {symbol}
                  </h2>
                  <p className={`text-base font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {getCoinName()}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className={`p-3 rounded-xl transition-all duration-200 hover:scale-110 ${
                  isDark
                    ? 'hover:bg-white/10 bg-white/5 text-gray-300 hover:text-white'
                    : 'hover:bg-gray-200 bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className={`inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent ${
                  isDark ? 'border-emerald-400' : 'border-orange-500'
                }`}></div>
                <p className={`mt-4 text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Loading coin data...
                </p>
              </div>
            ) : error ? (
              <div className={`text-center py-16 ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-semibold">{error}</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Hedge Performance vs BTC */}
                {coinData.hedgeBtc && (
                  <div className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                    isDark
                      ? 'bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent border-blue-500/30 hover:border-blue-500/50'
                      : 'bg-gradient-to-br from-blue-50 to-white border-blue-200 hover:border-blue-300'
                  }`}>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="relative">
                      <div className="flex items-center space-x-3 mb-5">
                        <div className={`p-2.5 rounded-xl ${
                          isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                        }`}>
                          <Shield className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Hedge Performance (vs BTC)
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Hedge Score</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            {coinData.hedgeBtc.hedge_score?.toFixed(1) || 'N/A'}
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Hit Rate</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeBtc.hit_rate?.toFixed(1) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Return</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeBtc.avg_return_on_btc_down_days?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Correlation</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeBtc.correlation_with_btc?.toFixed(2) || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hedge Performance vs ETH */}
                {coinData.hedgeEth && (
                  <div className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                    isDark
                      ? 'bg-gradient-to-br from-purple-500/10 via-purple-500/5 to-transparent border-purple-500/30 hover:border-purple-500/50'
                      : 'bg-gradient-to-br from-purple-50 to-white border-purple-200 hover:border-purple-300'
                  }`}>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/0 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="relative">
                      <div className="flex items-center space-x-3 mb-5">
                        <div className={`p-2.5 rounded-xl ${
                          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                        }`}>
                          <Shield className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                        </div>
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Hedge Performance (vs ETH)
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Hedge Score</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                            {coinData.hedgeEth.hedge_score?.toFixed(1) || 'N/A'}
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Hit Rate</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeEth.hit_rate?.toFixed(1) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Return</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeEth.avg_return_on_eth_down_days?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Correlation</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.hedgeEth.correlation_with_eth?.toFixed(2) || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bull Market Support Band USD */}
                {coinData.bmsbUsd && (
                  <div className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                    isDark
                      ? 'bg-gradient-to-br from-orange-500/10 via-orange-500/5 to-transparent border-orange-500/30 hover:border-orange-500/50'
                      : 'bg-gradient-to-br from-orange-50 to-white border-orange-200 hover:border-orange-300'
                  }`}>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500/0 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="relative">
                      <div className="flex items-center space-x-3 mb-5">
                        <div className={`p-2.5 rounded-xl ${
                          isDark ? 'bg-orange-500/20' : 'bg-orange-100'
                        }`}>
                          <Activity className={`w-6 h-6 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                        </div>
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Bull Market Support Band (USD)
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Position</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                            {coinData.bmsbUsd.position || 'N/A'}
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Price</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            ${coinData.bmsbUsd.current_price_usd?.toLocaleString() || 'N/A'}
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>% from Upper</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.bmsbUsd.pct_from_upper?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>% from Lower</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.bmsbUsd.pct_from_lower?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Price Comparison vs BTC */}
                {coinData.priceCompBtc && (
                  <div className={`group relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
                    isDark
                      ? 'bg-gradient-to-br from-yellow-500/10 via-yellow-500/5 to-transparent border-yellow-500/30 hover:border-yellow-500/50'
                      : 'bg-gradient-to-br from-yellow-50 to-white border-yellow-200 hover:border-yellow-300'
                  }`}>
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-yellow-500/0 to-yellow-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                    <div className="relative">
                      <div className="flex items-center space-x-3 mb-5">
                        <div className={`p-2.5 rounded-xl ${
                          isDark ? 'bg-yellow-500/20' : 'bg-yellow-100'
                        }`}>
                          <BarChart3 className={`w-6 h-6 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                        </div>
                        <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Price Performance vs BTC
                        </h3>
                      </div>
                      <div className="grid grid-cols-2 gap-5">
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Price Change</div>
                          <div className={`text-2xl font-bold ${
                            coinData.priceCompBtc.price_change_pct >= 0
                              ? (isDark ? 'text-emerald-400' : 'text-green-600')
                              : (isDark ? 'text-red-400' : 'text-red-600')
                          }`}>
                            {coinData.priceCompBtc.price_change_pct?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Outperformance</div>
                          <div className={`text-2xl font-bold ${
                            coinData.priceCompBtc.outperformance >= 0
                              ? (isDark ? 'text-emerald-400' : 'text-green-600')
                              : (isDark ? 'text-red-400' : 'text-red-600')
                          }`}>
                            {coinData.priceCompBtc.outperformance?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Correlation</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.priceCompBtc.correlation_with_btc?.toFixed(2) || 'N/A'}
                          </div>
                        </div>
                        <div className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                          <div className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Volatility</div>
                          <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {coinData.priceCompBtc.volatility?.toFixed(2) || 'N/A'}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* No data message */}
                {Object.keys(coinData).length === 0 && (
                  <div className={`text-center py-16 rounded-2xl border-2 ${
                    isDark
                      ? 'bg-white/5 border-white/10'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      No Data Available
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      No analysis data available for {symbol}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinProfileModal;
